<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="admins-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Administratorzy</h1>
      </div>
    </div>

    <!-- Banner: pending deletion requests (only for head admin) -->
    <v-alert
      v-if="isHeadAdmin && deletionRequests.length > 0"
      type="warning"
      prominent
      border="left"
      class="mb-4"
    >
      <div class="d-flex align-center">
        <div class="flex-grow-1">
          <strong>Oczekujące żądania usunięcia kont:</strong>
          {{ deletionRequests.length }}
        </div>
        <v-btn small color="warning" @click="deletionDialog = true">
          <v-icon left small>mdi-shield-alert</v-icon>
          Pokaż żądania
        </v-btn>
      </div>
    </v-alert>

    <!-- Dialog: deletion requests -->
    <v-dialog v-model="deletionDialog" max-width="700">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-shield-alert</v-icon>
          Żądania usunięcia kont admin
        </v-card-title>
        <v-card-text>
          <div v-if="!deletionRequests.length" class="text-center grey--text py-4">
            Brak oczekujących żądań
          </div>
          <v-list v-else>
            <v-list-item v-for="r in deletionRequests" :key="r.id" two-line>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>{{ r.target.firstName }} {{ r.target.lastName }}</strong>
                  <span class="caption grey--text ml-2">({{ r.target.email }})</span>
                </v-list-item-title>
                <v-list-item-subtitle>
                  Zażądał: {{ r.requestedBy.firstName }} {{ r.requestedBy.lastName }}
                  ({{ r.requestedBy.email }}) — {{ formatDate(r.createdAt) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="d-flex flex-row" style="gap: 8px;">
                <v-btn x-small color="success" @click="approveDeletion(r.id)" :loading="actionLoading === r.id">
                  <v-icon x-small left>mdi-check</v-icon>Zatwierdź
                </v-btn>
                <v-btn x-small color="error" outlined @click="rejectDeletion(r.id)" :loading="actionLoading === r.id">
                  <v-icon x-small left>mdi-close</v-icon>Odrzuć
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deletionDialog = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Trigger Button for Dialog -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="companyAdmins"
          class="elevation-1"
          :loading="loading"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-shield-account</v-icon></div>
              <div class="filter-title">{{ $t('admins.title') }}</div>
              <v-spacer></v-spacer>
              <button class="action-pill action-pill--primary" @click="
                  (dialog = true),
                    (action = 'Tworzenie'),
                    (currentCompanyAdminId = null),
                    (formData.subCompanyIds = subCompanies.map(s => s.id))
                ">
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('common.add') }}
              </button>
            </div>
          </template>
          <template v-slot:item.role="{ item }">
            <v-chip
              :color="roleChipColor(item.role)"
              text-color="white"
              small
            >
              {{ roleLabel(item.role) }}
            </v-chip>
          </template>
          <template v-slot:item.details="{ item }">
            <v-btn class="warning" @click="infoCompanyAdmin(item.id)"
              ><v-icon small class="pt-1">mdi-pencil</v-icon></v-btn
            >
            <v-btn
              v-if="isHeadAdmin && item.id !== myId"
              x-small outlined color="indigo" class="ml-1"
              :loading="impersonatingId === item.id"
              title="Wejdź jako ten administrator (podgląd konta)"
              @click="impersonateAdmin(item)"
            >
              <v-icon x-small left>mdi-account-switch</v-icon>Wejdź jako
            </v-btn>
            <v-btn
              v-if="isHeadAdmin"
              x-small outlined :color="item.isProtected ? 'amber darken-3' : 'grey'" class="ml-1"
              :loading="protectingId === item.id"
              :title="item.isProtected ? 'Zdejmij ochronę konta' : 'Oznacz konto jako chronione'"
              @click="toggleProtection(item)"
            >
              <v-icon x-small left>{{ item.isProtected ? 'mdi-lock' : 'mdi-lock-open-variant' }}</v-icon>
              {{ item.isProtected ? 'Chronione' : 'Chroń' }}
            </v-btn>
          </template>
          <template v-slot:item.deleteCompanyAdmin="{ item }">
            <v-btn class="error" @click="deleteCompanyAdmin(item.id)"
              ><v-icon small class="pt-1">mdi-delete</v-icon></v-btn
            >
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <!-- Form Dialog -->
    <v-dialog v-model="dialog" max-width="600px" content-class="dark-form-dialog">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ $t('admins.formTitle') }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
            <v-row>
              <!-- First Name -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.firstName"
                  :label="$t('admins.firstName')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- Last Name -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.lastName"
                  :label="$t('admins.lastName')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- Email -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.email"
                  label="Email"
                  :rules="[rules.required, rules.email]"
                  required
                />
              </v-col>

              <!-- Password -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.password"
                  :label="$t('admins.password')"
                  type="password"
                />
              </v-col>

              <!-- Password -->
              <v-col cols="12" md="12">
                <v-text-field
                  v-model="formData.phone"
                  :label="$t('admins.phone')"
                  type="phone"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- Company Select (UUID) -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.companyId"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  :label="$t('admins.mainCompany')"
                  :rules="[rules.required]"
                  required
                  :hint="$t('admins.mainCompanyHint')"
                  persistent-hint
                />
              </v-col>

              <!-- Access to all companies -->
              <v-col cols="12" md="6">
                <v-switch
                  v-model="formData.hasAccessToAllCompanies"
                  :label="$t('admins.accessAllCompanies')"
                  :hint="$t('admins.accessAllCompaniesHint')"
                  persistent-hint
                  color="primary"
                />
              </v-col>

              <!-- Additional allowed companies (multi-select) -->
              <v-col cols="12" md="12" v-if="!formData.hasAccessToAllCompanies">
                <v-select
                  v-model="formData.allowedCompanyIds"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  :label="$t('admins.additionalCompanies')"
                  multiple
                  chips
                  deletable-chips
                  :hint="$t('admins.additionalCompaniesHint')"
                  persistent-hint
                />
              </v-col>
              <!-- Subcompany IDs (Multi-select) -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.subCompanyIds"
                  :items="subCompanies"
                  item-text="name"
                  item-value="id"
                  :label="$t('admins.branch')"
                  multiple
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              <!-- Department IDs (Multi-select) -->
              <!-- <v-col cols="12" md="12">
                <v-select
                  v-model="formData.departmentIds"
                  :items="departments"
                  item-text="name"
                  item-value="id"
                  label="Departament"
                  multiple
                  :rules="[rules.required]"
                  required
                />
              </v-col> -->

              <!-- Role -->
              <v-col cols="12" md="12">
                <v-select
                  v-model="formData.role"
                  :items="roleOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('admins.role')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- Faksymilka (auto-podpis pism) -->
              <v-col cols="12" md="12">
                <v-checkbox
                  v-model="formData.canUseFacsimile"
                  label="Może używać faksymilki (auto-podpis pism)"
                  hide-details
                  class="mt-0"
                />
                <div class="caption grey--text">Pracownik wgrywa własny podpis w „Ustawieniach konta". Bez tego zawsze podpisuje ręcznie.</div>
              </v-col>

              <!-- Przypisywanie zadań -->
              <v-col cols="12" md="12">
                <v-checkbox
                  v-model="formData.canAssignTasks"
                  label="Może przypisywać zadania konkretnym osobom"
                  hide-details
                  class="mt-0"
                />
                <div class="caption grey--text">Bez tego zadania przypisywać mogą tylko Head Admin i Super Admin.</div>
              </v-col>

              <!-- Zmiana pól rozliczeniowych kierowcy -->
              <v-col cols="12" md="12">
                <v-checkbox
                  v-model="formData.canEditSettlementFields"
                  label="Może zmieniać dane rozliczeniowe kierowcy (typ wypłaty, częstotliwość, rodzaj współpracy, ZUS)"
                  hide-details
                  class="mt-0"
                />
                <div class="caption grey--text">Bez tego pola są wyszarzone — a że są wymagane, pracownik nie zapisze karty nowego kierowcy (nawet adresu). „Zastosuj natychmiast" zostaje tylko dla Head Admina: zmiana wchodzi od poniedziałku.</div>
              </v-col>

              <!-- Pages: access + edit per page -->
              <v-col cols="12" md="12">
                <div class="pages-perm-head">
                  <div class="pages-perm-title">{{ $t('admins.pagePermissions') }}</div>
                  <div class="pages-perm-actions">
                    <v-btn small text color="primary" @click="toggleAllPages(true, true)">
                      <v-icon small left>mdi-check-all</v-icon>Zaznacz wszystko (podgląd + edycja)
                    </v-btn>
                    <v-btn small text @click="toggleAllPages(true, false)">
                      <v-icon small left>mdi-eye-check-outline</v-icon>{{ $t('admins.previewAll') }}
                    </v-btn>
                    <v-btn small text @click="toggleAllPages(false, false)">
                      <v-icon small left>mdi-eye-off-outline</v-icon>{{ $t('admins.clear') }}
                    </v-btn>
                  </div>
                </div>
                <div class="pages-perm-table">
                  <div class="pages-perm-row pages-perm-row--head">
                    <span>{{ $t('admins.page') }}</span>
                    <span>{{ $t('admins.preview') }}</span>
                    <span>{{ $t('admins.editPerm') }}</span>
                  </div>
                  <div
                    v-for="p in availablePages"
                    :key="p.value"
                    class="pages-perm-row"
                    :class="{ 'pages-perm-row--sub': p.parent }"
                  >
                    <span class="pages-perm-name">
                      <v-icon v-if="p.parent" small color="grey" class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                      {{ p.text }}
                    </span>
                    <v-checkbox
                      :input-value="formData.pagesWithAccess.includes(p.value)"
                      hide-details
                      class="ma-0 pa-0 pages-perm-cb"
                      @change="togglePageAccess(p.value, $event)"
                    />
                    <v-checkbox
                      :input-value="formData.pagesWithEdit.includes(p.value)"
                      :disabled="!formData.pagesWithAccess.includes(p.value)"
                      hide-details
                      class="ma-0 pa-0 pages-perm-cb"
                      @change="togglePageEdit(p.value, $event)"
                    />
                  </div>
                </div>
                <div class="pages-perm-hint">
                  {{ $t('admins.superadminHint') }}
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Cancel Button -->
          <v-btn text color="secondary" @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <!-- Submit Button -->
          <v-btn color="primary" @click="submitForm" :disabled="!valid">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");
export default {
  data() {
    return {
      impersonatingId: null,
      protectingId: null,
      companyAdmins: [],
      deletionRequests: [],
      deletionDialog: false,
      actionLoading: null,
      headers: [
        {
          text: "Imię i Nazwisko",
          align: "start",
          sortable: true,
          value: "fullName",
        },
        {
          text: "Mail",
          align: "start",
          sortable: false,
          value: "email",
        },
        {
          text: "Rola",
          align: "start",
          sortable: true,
          value: "role",
        },
        {
          text: "Firma",
          align: "start",
          sortable: false,
          value: "companyName",
        },
        {
          text: "Departamenty",
          align: "start",
          sortable: false,
          value: "subcompanyNames",
        },
        { text: "", value: "details", sortable: false },
        { text: "", value: "deleteCompanyAdmin", sortable: false },
      ],
      loading: false,
      dialog: false,
      valid: false,
      formData: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phone: "",
        companyId: null,
        allowedCompanyIds: [],
        hasAccessToAllCompanies: false,
        departmentIds: [],
        subCompanyIds: [],
        pagesWithAccess: [],
        pagesWithEdit: [],
        role: "",
        canUseFacsimile: false,
        canAssignTasks: false,
        canEditSettlementFields: false,
      },
      companies: [],
      departments: [],
      subCompanies: [],
      // Strony dostępne do nadawania uprawnień. Pole `parent` na sub-tabach
      // wskazuje że jest to podzakładka konkretnej strony — UI renderuje
      // sub-taby z wcięciem pod rodzicem, można dać dostęp niezależnie.
      // Wartość w `value` używa kropkowej notacji `<parent>.<subtab>`.
      availablePages: [
        { text: "Dashboard", value: "dashboard" },
        { text: "Firma", value: "company" },
        { text: "Oddziały", value: "subcompanies" },
        { text: "Konta Bankowe", value: "bank-accounts" },
        { text: "Administratorzy", value: "admins" },
        { text: "Flota", value: "fleet" },
        { text: "Szkody", value: "crashes" },
        { text: "Likwidacja szkód", value: "claims" },
        { text: "Karty Paliwowe", value: "fuel-cards" },
        { text: "Inspekcje", value: "inspections" },
        { text: "Kierowcy", value: "drivers" },
        { text: "Archiwum", value: "drivers-archive" },
        { text: "Bonusy", value: "bonuses" },
        { text: "Kierowcy do Akceptacji", value: "driver-onboarding" },
        { text: "Oczekujące Zmiany Kierowców", value: "pending-drivers-changes" },
        { text: "Księgowość", value: "accounting" },
        { text: "FV do Akceptacji", value: "invoices" },
        { text: "FV Kosztowe", value: "cost-invoices" },
        { text: "Przychody", value: "incomes" },
        { text: "Wzory", value: "uploader-templates" },
        { text: "Importer", value: "uploader" },
        { text: "Historia Importu", value: "import-history" },
        { text: "Rozliczenia", value: "settlements" },
        { text: "Zleceniówka", value: "contracts" },
        { text: "Wypłaty", value: "payouts" },
        { text: "Lista dłużników", value: "debtors" },
        { text: "Powiadomienia", value: "notifications" },
        { text: "Wykaz Pojazdów", value: "wykaz-pojazdy" },
        { text: "Wykaz Kierowców", value: "wykaz-kierowcy" },
        { text: "Aktywność pracowników", value: "staff-activity" },
        { text: "Logi Zdarzeń", value: "audit-logs" },
        // === Strony wcześniej pominięte w uprawnieniach (dodane, by dało się
        // nadać/odebrać dostęp niższym rolom; head/superadmin mają pełny dostęp) ===
        { text: "Flota Kierowców", value: "driver-fleet" },
        { text: "Wypłaty dzienne", value: "daily-payouts" },
        { text: "Wypłaty BLIK", value: "self-service-payouts" },
        { text: "Rachunki", value: "receipts" },
        { text: "Raport kasowy", value: "cash-report" },
        { text: "Obsługa komornicza", value: "komornik-letters" },
        { text: "Sprzedaż polis", value: "policy-sales" },
        { text: "Wzory Dokumentów", value: "document-templates" },
        { text: "Oczekujące Dokumenty", value: "pending-documents" },
        { text: "Dokumenty urzędowe", value: "official-documents" },
        { text: "Licencje TAXI", value: "company-licenses" },
        { text: "Mapa i historia lokalizacji kierowców", value: "driver-map" },
        { text: "Legalizacja pobytu i pracy", value: "legalization" },
        { text: "Posty i grafiki", value: "marketing-posts" },
        { text: "Zatwierdzanie grafik", value: "marketing-posts.zatwierdzanie", parent: "marketing-posts" },
        { text: "Ewidencja czasu pracy", value: "work-time" },
        { text: "Zadania", value: "tasks" },
        { text: "Logi Powiadomień", value: "notification-logs" },
        { text: "Czat", value: "chat" },
        { text: "Komunikaty", value: "announcements" },
        { text: "Zgłoszenia ze strony", value: "website-leads" },
        { text: "SMS", value: "sms" },
        { text: "E-mail", value: "email" },
        { text: "Konfiguracja Wypłat", value: "payout-config" },
        { text: "Wiki", value: "wiki" },
        { text: "Ustawienia systemu", value: "system-settings" },
        { text: "Punkty – Ustawienia", value: "points-settings" },
        { text: "Punkty – Polecenia", value: "points-referrals" },
        { text: "Punkty – Opinie", value: "points-reviews" },
        { text: "Punkty – Zamówienia Nagród", value: "points-reward-orders" },
        { text: "Punkty – Salda Kierowców", value: "points-drivers" },
        // === Integracje API z podzakładkami ===
        { text: "Integracje API", value: "integrations" },
        { text: "Bolt", value: "integrations.bolt", parent: "integrations" },
        { text: "Uber", value: "integrations.uber", parent: "integrations" },
        { text: "FreeNow", value: "integrations.freenow", parent: "integrations" },
        // === Repozytorium dokumentów z podzakładkami ===
        { text: "Repozytorium dokumentów", value: "documents-repository" },
        { text: "Firmowe", value: "documents-repository.company", parent: "documents-repository" },
        { text: "Podpisane przez kierowców", value: "documents-repository.signed-by-drivers", parent: "documents-repository" },
        // === Bolt Sync z podzakładkami ===
        { text: "Bolt Sync", value: "bolt-sync-drivers" },
        { text: "Kierowcy (Bolt)", value: "bolt-sync-drivers.drivers", parent: "bolt-sync-drivers" },
        { text: "Pojazdy (Bolt)", value: "bolt-sync-drivers.vehicles", parent: "bolt-sync-drivers" },
        // === Szczegóły pojazdu (Flota) z podzakładkami ===
        { text: "Szczegóły pojazdu", value: "fleet-details" },
        { text: "Licencje", value: "fleet-details.licenses", parent: "fleet-details" },
        { text: "Protokół Najmu", value: "fleet-details.rent-protocol", parent: "fleet-details" },
        { text: "Protokół Zakończenia", value: "fleet-details.end-rent", parent: "fleet-details" },
        { text: "Serwis", value: "fleet-details.service", parent: "fleet-details" },
        { text: "Wypadki", value: "fleet-details.crash", parent: "fleet-details" },
        // === Szczegóły kierowcy z podzakładkami ===
        { text: "Szczegóły kierowcy", value: "driver-details" },
        { text: "Ogólne", value: "driver-details.general", parent: "driver-details" },
        { text: "Dokumenty (kierowca)", value: "driver-details.documents", parent: "driver-details" },
        { text: "FV (kierowca)", value: "driver-details.invoices", parent: "driver-details" },
        { text: "Bonusy (kierowca)", value: "driver-details.bonuses", parent: "driver-details" },
        { text: "Najem auta (kierowca)", value: "driver-details.car-rental", parent: "driver-details" },
        { text: "Dane importu (kierowca)", value: "driver-details.import-data", parent: "driver-details" },
        { text: "Wypłaty (kierowca)", value: "driver-details.payouts", parent: "driver-details" },
        { text: "Pojazdy (kierowca)", value: "driver-details.cars", parent: "driver-details" },
        { text: "Komornicy (kierowca)", value: "driver-details.bailiffs", parent: "driver-details" },
        { text: "Historia (kierowca)", value: "driver-details.history", parent: "driver-details" },
      ],
      roleOptions: [
        { text: "Super Administrator", value: "superadmin" },
        { text: "Office", value: "office" },
        { text: "Manager", value: "user" },
      ],
      rules: {
        required: (value) => !!value || "Wymagane.",
        email: (value) => /.+@.+\..+/.test(value) || "Nieprawidłowy email.",
      },
    };
  },
  async created() {
    await this.initialize();
    if (this.isHeadAdmin) await this.fetchDeletionRequests();
  },
  computed: {
    isHeadAdmin() {
      // TYLKO właściciel (headadmin) — „wejdź jako" i wnioski o usunięcie kont
      return localStorage.getItem('actualRole') === 'headadmin';
    },
    myId() {
      try {
        const t = localStorage.getItem('token');
        const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return p.id || (p.user && p.user.id) || null;
      } catch (e) {
        return null;
      }
    },
  },
  methods: {
    async impersonateAdmin(item) {
      this.impersonatingId = item.id;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'admin-auth/impersonate/admin/' + item.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const d = res.data;
        localStorage.setItem('token', d.access_token);
        localStorage.setItem('role', d.role);
        localStorage.setItem('actualRole', d.actualRole || d.role || '');
        localStorage.setItem('isHead', String(!!d.isHead));
        localStorage.setItem('pages', JSON.stringify(d.pages || []));
        localStorage.setItem('pagesWithEdit', JSON.stringify(d.pagesWithEdit || []));
        if (d.companyId) localStorage.setItem('activeCompanyId', d.companyId);
        if (d.companyName) localStorage.setItem('activeCompanyName', d.companyName);
        window.location.href = '/';
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wejść na konto', 'error');
        this.impersonatingId = null;
      }
    },
    async toggleProtection(item) {
      const enabling = !item.isProtected;
      const confirm = await Swal.fire({
        title: enabling ? 'Włączyć ochronę konta?' : 'Zdjąć ochronę konta?',
        html: enabling
          ? `Konto <b>${item.fullName}</b> będzie chronione — inni administratorzy nie będą mogli go edytować ani usunąć.`
          : `Z konta <b>${item.fullName}</b> zostanie zdjęta ochrona — będzie mogło być edytowane/usuwane przez administratorów.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: enabling ? 'Włącz ochronę' : 'Zdejmij ochronę',
        cancelButtonText: 'Anuluj',
      });
      if (!confirm.isConfirmed) return;
      this.protectingId = item.id;
      try {
        await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'company-admins/' + item.id + '/protection',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { isProtected: enabling },
        });
        item.isProtected = enabling;
        Swal.fire({ title: enabling ? 'Ochrona włączona' : 'Ochrona zdjęta', icon: 'success', timer: 1300, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zmienić ochrony konta', 'error');
      } finally {
        this.protectingId = null;
      }
    },
    formatDate(d) {
      try { return new Date(d).toLocaleString('pl-PL'); } catch { return d; }
    },
    async fetchDeletionRequests() {
      try {
        const r = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'company-admins/deletion-requests',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.deletionRequests = r.data.data || [];
      } catch (e) { /* ignore — non-head adminowi nie pokazujemy */ }
    },
    async approveDeletion(id) {
      this.actionLoading = id;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + `company-admins/deletion-requests/${id}/approve`,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        await this.fetchDeletionRequests();
        await this.initialize(); // odśwież listę admin-ów
        Swal.fire('Zatwierdzono', 'Konto zostało usunięte.', 'success');
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się', 'error');
      }
      this.actionLoading = null;
    },
    async rejectDeletion(id) {
      const r = await Swal.fire({
        title: 'Odrzucić żądanie?',
        input: 'text',
        inputLabel: 'Powód odrzucenia (opcjonalny)',
        showCancelButton: true,
        confirmButtonText: 'Odrzuć',
        cancelButtonText: 'Anuluj',
      });
      if (!r.isConfirmed) return;
      this.actionLoading = id;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + `company-admins/deletion-requests/${id}/reject`,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { rejectionReason: r.value || undefined },
        });
        await this.fetchDeletionRequests();
        Swal.fire('Odrzucone', 'Żądanie zostało odrzucone.', 'success');
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się', 'error');
      }
      this.actionLoading = null;
    },
    roleLabel(role) {
      const map = {
        headadmin: "Head Admin",
        superadmin: "Super Admin",
        office: "Office",
        user: "Manager",
      };
      if (map[role]) return map[role];
      if (!role) return "—";
      // Pierwsza litera duża dla nieznanych ról (np. "manager" → "Manager")
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    },
    roleChipColor(role) {
      const map = {
        headadmin: "#D32F2F",  // czerwony — najwyższe uprawnienia
        superadmin: "#388E3C", // zielony — superadmin
        office: "#00897B",
        user: "#7E57C2",
      };
      return map[role] || "#546E7A";
    },
    // Mapa zależności: gdy admin daje access do KLUCZA, automatycznie dorzucamy
    // strony WARTOŚCI (do których klucz drąży). Inaczej user dostaje "wypłaty"
    // ale nie może kliknąć na nazwisko kierowcy bo to prowadzi na driver-details
    // które wymaga osobnego uprawnienia.
    pageAccessDependencies() {
      return {
        // Wypłaty drążą w kierowców (klik nazwisko)
        'payouts': ['drivers', 'driver-details'],
        'daily-payouts': ['drivers', 'driver-details'],
        'self-service-payouts': ['drivers', 'driver-details'],
        // Rozliczenia → kierowca + faktury kosztowe per rozliczenie
        'settlements': ['drivers', 'driver-details', 'invoices', 'cost-invoices'],
        // Rachunki → kierowca
        'receipts': ['drivers', 'driver-details'],
        // Flota → szczegóły auta (FleetDetailsPage)
        'fleet': ['fleet-details', 'crashes', 'crash-history'],
        'driver-fleet': ['fleet-details'],
        'crashes': ['crash-history', 'driver-details', 'fleet-details'],
        // Kierowcy → szczegóły + dokumenty
        'drivers': ['driver-details', 'pending-documents', 'pending-drivers-changes'],
        'drivers-archive': ['driver-details'],
        // Faktury → kierowca
        'invoices': ['drivers', 'driver-details'],
        'cost-invoices': ['drivers', 'driver-details'],
        'incomes': ['drivers', 'driver-details'],
        // Import → historia + skipped
        'uploader': ['import-history', 'skipped-import'],
        'import-history': ['skipped-import'],
        // Cash report → kierowca (do filtrów)
        'cash-report': ['drivers'],
        // Integracje API → bolt-sync (link "Powiązane narzędzia")
        'integrations': ['bolt-sync-drivers'],
        // Punkty → kierowca
        'points-drivers': ['drivers', 'driver-details'],
        'points-reviews': ['drivers'],
        'points-referrals': ['drivers'],
        'points-reward-orders': ['drivers'],
        // Dokumenty urzędowe → wyszukiwanie i wybór kierowcy/właściciela
        'official-documents': ['drivers'],
        'company-licenses': [],
        // Karta legalizacji czyta dane kierowcy — bez dostępu do kierowców
        // strona pokazałaby puste zestawienie.
        'legalization': ['drivers'],
      };
    },
    toggleAllPages(accessChecked, alsoEdit = false) {
      if (accessChecked) {
        this.formData.pagesWithAccess = this.availablePages.map((p) => p.value);
        if (alsoEdit) {
          this.formData.pagesWithEdit = this.availablePages.map((p) => p.value);
        }
      } else {
        this.formData.pagesWithAccess = [];
        this.formData.pagesWithEdit = [];
      }
    },
    togglePageAccess(pageValue, checked) {
      const access = new Set(this.formData.pagesWithAccess);
      const edit = new Set(this.formData.pagesWithEdit);
      const page = this.availablePages.find((p) => p.value === pageValue);
      if (checked) {
        access.add(pageValue);
        // Auto-dorzuć strony do których ta drąży
        const deps = this.pageAccessDependencies()[pageValue] || [];
        for (const dep of deps) {
          if (this.availablePages.some((p) => p.value === dep)) {
            access.add(dep);
          }
        }
        // Jeśli to sub-tab — automatycznie dodaj też rodzica, inaczej user
        // nie zobaczy strony w menu w ogóle.
        if (page?.parent) {
          access.add(page.parent);
        }
      } else {
        access.delete(pageValue);
        edit.delete(pageValue);
        // Jeśli odznaczamy rodzica — odznaczamy też wszystkie jego sub-taby.
        if (!page?.parent) {
          for (const p of this.availablePages) {
            if (p.parent === pageValue) {
              access.delete(p.value);
              edit.delete(p.value);
            }
          }
        }
      }
      this.formData.pagesWithAccess = Array.from(access);
      this.formData.pagesWithEdit = Array.from(edit);
    },
    togglePageEdit(pageValue, checked) {
      const edit = new Set(this.formData.pagesWithEdit);
      if (checked) {
        if (!this.formData.pagesWithAccess.includes(pageValue)) return;
        edit.add(pageValue);
      } else {
        edit.delete(pageValue);
      }
      this.formData.pagesWithEdit = Array.from(edit);
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async getCompanySelect() {
      await this.toggleLoading(true);
      try {
        const companies = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `company`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.companies = companies.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async deleteCompanyAdmin(id) {
      Swal.fire({
        title: "Napewno usunąć?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "delete",
              url: process.env.VUE_APP_ROOT_API + "company-admins/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response.data.message.toString(), "error");
          }
        }
      });
    },
    async getSubcompanySelect() {
      await this.toggleLoading(true);
      try {
        const subCompanies = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `registry/vehicles/sub-companies`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.subCompanies = subCompanies.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async submitForm() {
      // Validate the form
      if (this.$refs.form.validate()) {
        try {
          await this.toggleLoading(true);
          if (this.currentCompanyAdminId) {
            await axios({
              method: "patch",
              url:
                process.env.VUE_APP_ROOT_API +
                "company-admins/" +
                this.currentCompanyAdminId,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
              data: {
                ...this.formData,
                password:
                  this.formData.password?.trim().length > 0
                    ? this.formData.password
                    : null,
              },
            });
            if (this.formData.password?.trim().length > 0) {
              Swal.fire("Zapisano zmiany", "Hasło zostało zmienione.", "warning");
            } else {
              Swal.fire({ title: "Zapisano zmiany", icon: "success", timer: 1800, showConfirmButton: false });
            }
          } else {
            await axios({
              method: "post",
              url: process.env.VUE_APP_ROOT_API + "company-admins",
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
              data: {
                ...this.formData,
              },
            });
            Swal.fire({ title: "Administrator dodany", icon: "success", timer: 1800, showConfirmButton: false });
          }

          this.closeDialog();
          await this.toggleLoading(false);
          await this.initialize();
        } catch (e) {
          await this.toggleLoading(false);
          console.log(e);
          Swal.fire("Błąd", e.response.data.message.toString(), "error");
        }
      }
    },
    closeDialog() {
      this.dialog = false;
      this.currentCompanyAdminId = undefined;
      this.$refs.form.resetValidation(); // Reset form validation
    },
    async infoCompanyAdmin(id) {
      await this.toggleLoading(true);
      try {
        const dep = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "company-admins/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.formData = dep.data.data;
        this.formData.subCompanyIds = dep.data.data.subcompanies.map(
          (it) => it.subcompany.id
        );
        this.formData.pagesWithAccess = dep.data.data.pagesWithAccess || [];
        this.formData.pagesWithEdit = dep.data.data.pagesWithEdit || [];
        this.formData.role = dep.data.data.role || "";
        this.formData.allowedCompanyIds = dep.data.data.allowedCompanyIds || [];
        this.formData.hasAccessToAllCompanies = dep.data.data.hasAccessToAllCompanies || false;

        this.currentCompanyAdminId = dep.data.data.id;
        this.dialog = true;
        await this.toggleLoading(false);
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
    },
    async initialize() {
      this.loading = true;
      await this.getCompanySelect();
      await this.getSubcompanySelect();
      try {
        const users = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `company-admins`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.companyAdmins = users.data.data.map((user) => {
          return {
            firstName: user.firstName,
            lastName: user.lastName,
            fullName: [user.firstName, user.lastName].filter(Boolean).join(" ") || "—",
            phone: user.phone,
            email: user.email,
            id: user.id,
            role: user.role || "",
            isProtected: !!user.isProtected,
            companyName: user.company.name,
            subcompanyNames: user.subcompanies
              .map((it) => it.subcompany.name)
              .toString(),
            departamentNames: user.departments
              .map((it) => it.department.name)
              .toString(),
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.admins-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-pill--primary { background: #5478FF; color: #FFFFFF; }
.action-pill--primary:hover { background: #3F62E8; }
.admins-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px;
  border: 1px solid #2A3142;
}
.admins-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.admins-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.admins-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.admins-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.admins-page >>> .v-text-field input,
.admins-page >>> .v-text-field textarea,
.admins-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.admins-page >>> .v-label {
  color: #94A0B8 !important;
}
.admins-page >>> .v-dialog .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.admins-page >>> .v-card__title,
.admins-page >>> .v-card__text {
  color: #E6EAF2 !important;
}
.admins-page >>> .grey--text {
  color: #94A0B8 !important;
}

.pages-perm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.pages-perm-title {
  font-weight: 700;
  font-size: 15px;
  color: #E6EAF2;
}
.pages-perm-actions { display: flex; gap: 4px; }
.pages-perm-table {
  border: 1px solid #2A3142;
  border-radius: 6px;
  overflow: hidden;
  max-height: 340px;
  overflow-y: auto;
  background: #1F2433;
}
.pages-perm-row {
  display: grid;
  grid-template-columns: 1fr 100px 100px;
  align-items: center;
  padding: 8px 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  font-size: 14px;
  color: rgba(0,0,0,.87);
}
.pages-perm-row:last-child { border-bottom: 0; }
.pages-perm-row--head {
  background: #eef2f6;
  font-weight: 700;
  color: rgba(0,0,0,.75);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: .5px;
  position: sticky;
  top: 0;
  z-index: 1;
}
.pages-perm-row--head > span:nth-child(2),
.pages-perm-row--head > span:nth-child(3) { text-align: center; }
.pages-perm-name {
  color: rgba(0,0,0,.87);
  font-weight: 500;
}
.pages-perm-row--sub {
  background: rgba(0, 0, 0, 0.02);
}
.pages-perm-row--sub .pages-perm-name {
  padding-left: 24px;
  font-weight: 400;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
}
.theme--dark .pages-perm-row--sub {
  background: rgba(255, 255, 255, 0.02);
}
.theme--dark .pages-perm-row--sub .pages-perm-name {
  color: rgba(255, 255, 255, 0.7);
}
.pages-perm-cb { justify-self: center; }
.pages-perm-hint {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(0,0,0,.6);
}

/* Dark theme support */
.theme--dark .pages-perm-title,
.theme--dark .pages-perm-row,
.theme--dark .pages-perm-name { color: rgba(255,255,255,.92); }
.theme--dark .pages-perm-table { background: #1e1e1e; border-color: rgba(255,255,255,.2); }
.theme--dark .pages-perm-row { border-bottom-color: rgba(255,255,255,.08); }
.theme--dark .pages-perm-row--head { background: #2a2d34; color: rgba(255,255,255,.8); }
.theme--dark .pages-perm-hint { color: rgba(255,255,255,.65); }
</style>
